import React, { useEffect, useState } from "react";
import Topbar from "@/components/layout/Topbar";
import { api, formatMoney } from "@/lib/api";
import { toast, Toaster } from "sonner";

export default function FinesPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    setLoading(true);
    api
      .get("/records", { params: { record_type: "FINE", limit: 200 } })
      .then((r) => {
        setRows(r.data.items);
        setTotal(r.data.total);
      })
      .catch(() => toast.error("Failed to load fines"))
      .finally(() => setLoading(false));
  }, []);

  return (
    <>
      <Toaster position="top-right" richColors />
      <Topbar
        title="Fines"
        subtitle="Regulatory penalties ingested via Smartsheet webhook"
      />
      <div className="p-6 animate-fade-in" data-testid="fines-page">
        <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
          <StatCard label="Total fines" value={total.toLocaleString()} suffix="records" />
          <StatCard
            label="Total amount"
            value={formatMoney(rows.reduce((s, r) => s + (r.amount || 0), 0))}
          />
          <StatCard
            label="Latest"
            value={
              rows[0]?.date
                ? new Date(rows[0].date).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                  })
                : "—"
            }
          />
        </div>

        <div className="overflow-hidden rounded-md border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-[10px] font-semibold uppercase tracking-widest text-slate-500">
                <th className="px-5 py-3 text-left">Date</th>
                <th className="px-5 py-3 text-left">Company</th>
                <th className="px-5 py-3 text-left">Type</th>
                <th className="px-5 py-3 text-left">Description</th>
                <th className="px-5 py-3 text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={5} className="px-5 py-10 text-center text-slate-400">
                    Loading…
                  </td>
                </tr>
              )}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-5 py-10 text-center text-slate-400">
                    No fines recorded.
                  </td>
                </tr>
              )}
              {rows.map((r) => (
                <tr
                  key={r.id}
                  className="border-b border-slate-100 last:border-0 hover:bg-slate-50"
                  data-testid={`fine-row-${r.id}`}
                >
                  <td className="px-5 py-3 text-slate-500 tabular">
                    {new Date(r.date).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    })}
                  </td>
                  <td className="px-5 py-3 font-medium text-navy-600">
                    {r.companyName}
                  </td>
                  <td className="px-5 py-3">
                    <span className="rounded-sm bg-red-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest text-red-700">
                      {r.category || "Fine"}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-slate-500">{r.description || "—"}</td>
                  <td className="px-5 py-3 text-right font-semibold tabular text-brand-orange">
                    {formatMoney(r.amount, r.currency)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

function StatCard({ label, value, suffix }) {
  return (
    <div className="rounded-md border border-slate-200 bg-white p-4">
      <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">
        {label}
      </div>
      <div className="mt-1.5 font-display text-2xl font-semibold tracking-tight text-navy-600 tabular">
        {value}
        {suffix && (
          <span className="ml-1.5 text-xs font-medium text-slate-400">{suffix}</span>
        )}
      </div>
    </div>
  );
}
